import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  inject,
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
} from '@angular/core';
import { LocalStorage } from '@freelancer/local-storage';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Theme } from './theme.types';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private document = inject(DOCUMENT);
  private localStorage = inject(LocalStorage);

  public theme$: Observable<Theme>;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.theme$ = this.localStorage
        .get('theme')
        .pipe(map(theme => theme ?? 'light'));
    } else {
      this.theme$ = of('light');
    }
  }

  // Set the theme in local storage.
  async setTheme(theme: Theme): Promise<void> {
    await this.localStorage.set('theme', theme);
  }

  // Apply the theme to the document.
  applyTheme(theme: Theme): void {
    if (isPlatformBrowser(this.platformId)) {
      const toSet =
        theme === 'system'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
          : theme;

      if (toSet === 'dark') {
        this.renderer.addClass(this.document.documentElement, 'dark');
      } else if (this.document.documentElement.className.includes('dark')) {
        this.renderer.removeClass(this.document.documentElement, 'dark');
      }
    }
  }
}
